import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "./SelectCustomer.css";
import DefaultHeading from "../../components/heading/DefaultHeading";
import {
  CustomerInfo,
  OutletDetailsType,
} from "../../features/createSecondaryOrder/CreateSecondaryOrderType";
import { autocompleteLableMarginFix, customSx, generateBeatName, secondaryOrderDatePickerSx } from "./util";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OutletForm from "../../features/viewOrder/OutletForm";
import { Modal } from "@mui/material";
import { getOutletDetailsForSecondary } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { getConfigData } from "../../services/configurationService";
import { ConfigValue, CreateSecondaryOrderConfig } from "../../features/createSecondaryOrder/CreateSecondaryOrder";
import { OutletDetails } from "../../features/createSecondaryOrder/models";

const SelectCustomer: React.FC<CustomerInfo> = ({
  beatNameToIdMap,
  routeIdNameMap,
  routeIdDetails,
  orderType,
  selectedCustomerInfo,
  setSelectedCustomerInfo,
  outletsList,
  allOutletsList,
  beatList,
  beatRecord,
  salesRepDisplayNameToLoginIdMapping,
  expandForm,
  setExpandForm,
  salesReps,
  selectedBeat,
  setSelectedBeat,
  setFilteredBeats,
  selectedSalesRep,
  setSelectedSalesRep,
  setOutletDetails,
  setIsCounterSaleOrder,
  isCounterSaleOrder,
  setOutletApprovalSent,
  orderFullfillmentTime,
  setOutletReferenceId,
  createSecondaryOrderConfig,
  setSelectedOutletCode,
  selectedOutletCode
}) => {
  const DELIVERY_DATE = dayjs().add(orderFullfillmentTime ?? 2, "day").format("DD/MM/YY");
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    fetchPricingBySalesrep
  } = ConfigDictionary;
  const [counterSalesOutletFormModal, setCounterSalesOutletFormModal] = useState(false);
  const isCounterSaleDMSOutlet = useRef<boolean>(false);

  const isClearableEnabledOnBeat = (beatList?.length === 1) ? (outletsList?.length === 1) : !(beatList?.length === 0);
  const isBeatListLengthValid = beatList?.length <= 1 && !isClearableEnabledOnBeat;
  const isClearableEnabledOnOutlet = (fetchPricingBySalesrep ? ((outletsList?.length === 1) ? (beatList?.length === 1) : !(outletsList?.length === 0)) : true);
  const isOutletListLengthValid = (fetchPricingBySalesrep ? (outletsList?.length <= 1 && !isClearableEnabledOnOutlet) : outletsList?.length <= 1);

  useEffect(() => {
    const outlet: OutletDetails | null =
      outletsList?.find((outlet) => (outlet.outletCode === selectedCustomerInfo.outletCode || outlet.outletCode === selectedOutletCode)) ?? null;

    if (outlet) {
      setOutletReferenceId?.(outlet.id)
      isCounterSaleDMSOutlet.current = outlet?.extendedAttributes?.counterSalesOutletDMS ?? false
      const uniqueImmediateParents = Array.from(
        new Set(
          outlet?.immediateParent
            ?.split(",")
            ?.map((str: any) => str?.trim().toUpperCase())
        )
      );
      setSelectedCustomerInfo((prevData) => ({
        ...prevData,
        outletCode: selectedOutletCode ?? "",
        salesRep: (fetchPricingBySalesrep && fetchPricingBySalesrep) ? uniqueImmediateParents : outlet?.extendedAttributes?.["parent-0-0-id"] ?? "",
        selectedSalesRep: (fetchPricingBySalesrep && fetchPricingBySalesrep) ? selectedSalesRep : outlet?.extendedAttributes?.["parent-0-0-id"] ?? "",
        differentShippingAddress: outlet?.extendedAttributes?.shipTo?.address ?? "",
        shippingAddress: outlet?.address ?? "",
        customerChannel: outlet?.channel,
        paymentMethod: outlet?.extendedAttributes?.preferredPaymentMode ?? outlet?.extendedAttributes?.OutletPaymentmode ?? outlet.paymentMode ?? "",
        outletName: outlet?.outletName ?? "",
        activeStatus: outlet?.activeStatus ?? "",
        beat: outlet?.beat ?? "",
      }));
      if (isCounterSaleDMSOutlet.current) {
        if (outlet?.approvalStatus === "PENDING") setIsCounterSaleOrder?.(true)
      }
      else{
        setIsCounterSaleOrder?.(false)
      }
    } else {
      handleMultiValueChange({
        outletCode: "",
        beat: "",
        shippingAddress: "",
        customerChannel: "",
        paymentMethod: "",
        outletName: "",
      });
    }
  }, [selectedCustomerInfo?.outletCode, selectedSalesRep]);

  useEffect(() => {
    const updateCustomerInfoOnOutletChange = () => {
      setSelectedCustomerInfo((prev) => ({
        ...prev,
        salesType: prev?.salesType ?? "Direct Sales",
        billDate: prev?.billDate ?? dayjs().format("DD/MM/YY"),
        deliveryDate:
          prev?.deliveryDate ??
          DELIVERY_DATE
      }));
    };

    updateCustomerInfoOnOutletChange();
  }, [selectedCustomerInfo?.outletCode]);

  useEffect(() => {
    const setter = async() => {
      if(beatList?.length === 1) {
        setSelectedBeat(beatList[0]);
        handleMultiValueChange({
          beat: beatNameToIdMap?.[beatList[0]]
        });
      }
      if(setSelectedOutletCode && outletsList?.length === 1) {
        setSelectedOutletCode(outletsList[0]?.outletCode);
        handleMultiValueChange({
          outletCode: outletsList[0]?.outletCode
        });
      }
    }
    setter();
  }, [beatList, outletsList])

  const handleChange = async (key: string, newValue: any) => {
    if (key === "salesType" && newValue === "Counter Sales") {
      setCounterSalesOutletFormModal(true);
      return
    }
    if (key === "salesType" && newValue === "Direct Sales"){
      setSelectedCustomerInfo(
        {
            outletCode: "",
            salesRep: selectedSalesRep,
            salesType: "Direct Sales",
            billDate: dayjs().format("DD/MM/YY"),
            deliveryDate: DELIVERY_DATE
          }
      );
      return
    }
    setSelectedCustomerInfo((prevData) => ({
      ...prevData,
      [key]: newValue,
    }));
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };
  const handleCounterSalesOutletFormClose = () => {
    setCounterSalesOutletFormModal(false);
  };

const handleOutletCreate = async (createdOutlet: any, newRoute: string) => {
  if (!createdOutlet) {
    console.error("handleOutletCreate: createdOutlet is undefined or null.");
    return;
  }
  const immediateParent = createdOutlet.immediateParent.split(",")

  const matched = Object.values(salesRepDisplayNameToLoginIdMapping ?? {}).find(
    (value) => value?.toUpperCase() === immediateParent[0]?.toUpperCase()
  ) ?? null;

  setSelectedSalesRep?.(matched ?? "");
  
  //Need to set the unique name of beat. if the salesrep received has two beats with the same name then the beat name will be beatname-beatid
  const beatName = await generateBeatName(routeIdDetails, matched ?? "", newRoute)
  setSelectedBeat(beatName);

  setSelectedOutletCode?.(createdOutlet.outletCode)
  setSelectedCustomerInfo((prev) => ({
    ...prev,
    // map the fields from createdOutlet to selectedCustomerInfo
    outletCode: createdOutlet.outletCode ?? "",
    beat: createdOutlet?.beat?? "",
  }));
  isCounterSaleDMSOutlet.current = createdOutlet?.extendedAttributes?.counterSalesOutletDMS ?? false
  setOutletDetails?.([createdOutlet, ...outletsList])
  setIsCounterSaleOrder?.(true)
  setOutletApprovalSent?.(true)
};

  const handleMultiValueChange = (newKeyValuePairs: Record<string, any>) => {
    setSelectedCustomerInfo((prevData) => ({
      ...prevData,
      ...newKeyValuePairs,
    }));
  };

  const renderFieldMap: Record<keyof CreateSecondaryOrderConfig, (configValue: ConfigValue) => React.ReactNode> = {
    orderTypeLabel: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item"  xs={4} alignItems="center" >
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <FormControl fullWidth size="small" className="custom_form_control" sx={customSx}>
                    <InputLabel sx={customSx} id="order-type-label">
                      Select Here
                    </InputLabel>
                    <Select
                      labelId="order-type-label"
                      id="salesType"
                      value={selectedCustomerInfo.salesType || "Direct Sales"}
                      onChange={(e) => handleChange("salesType", e.target.value)}
                      className="select_customer_textfield"
                      label="Order Type"
                      size="small"
                      fullWidth
                      sx={customSx}
                    >
                      {orderType.map((salesType) => (
                        <MenuItem
                          key={salesType}
                          value={salesType}
                          sx={{
                            color: "#464646",
                            fontSize: "14px",
                          }}
                        >
                          {salesType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
      </Grid>
    ),
    salesRep: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                {(fetchPricingBySalesrep && fetchPricingBySalesrep && salesReps) ? (
                  <FormControl fullWidth size="small" className="custom_form_control">
                    <Autocomplete
                      options={salesReps}
                      value={
                        (() => {
                          if (!salesRepDisplayNameToLoginIdMapping || !selectedSalesRep) return null;
                          
                          // Find the display name that maps to the selectedSalesRep (loginId)
                          const displayName = Object.keys(salesRepDisplayNameToLoginIdMapping).find(key => salesRepDisplayNameToLoginIdMapping[key] === selectedSalesRep);
                          return displayName ? salesReps?.find(sr => sr === displayName) || null : null;
                        })()
                      }
                      onChange={(event, newValue) => {
                        if (newValue && setSelectedSalesRep) {
                          // Get loginId from the mapping
                          const loginId = salesRepDisplayNameToLoginIdMapping?.[newValue] || newValue;
                          setSelectedSalesRep(loginId)
                          setSelectedOutletCode?.("")
                          setSelectedBeat("");
                          if(setSelectedOutletCode) setSelectedOutletCode("");
                          setOutletDetails?.([]);
                          setFilteredBeats([]);
                          handleMultiValueChange({
                            selectedSalesRep: loginId,
                            outletCode: "",
                            beat: beatNameToIdMap?.[newValue],
                          })
                        } else if(newValue == null || newValue == undefined) {
                          setSelectedSalesRep?.('')
                          setSelectedBeat("");
                          setSelectedCustomerInfo({})
                          setSelectedOutletCode?.('')
                        } else if(setSelectedSalesRep) {
                          setSelectedBeat("");
                          setSelectedSalesRep("");
                          setOutletDetails?.([]);
                          handleMultiValueChange({
                            outletCode: "",
                            beat: "",
                            salesRep: "",
                            selectedSalesRep: "",
                            shippingAddress: "",
                            customerChannel: "",
                            paymentMethod: "",
                            outletName: "",
                          });
                        }
                      }}
                      sx={{ ...customSx, ...autocompleteLableMarginFix }}
                      renderOption={(props, option) => (
                        <li {...props} style={{ color: "#464646", fontSize: "14px" }}>
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="skuCode"
                          label="Select Here"
                          variant="outlined"
                          size="small"
                          sx={{ ...customSx, ...autocompleteLableMarginFix }}
                        />
                      )}
                    />
                  </FormControl>
                  ) : (<TextField
                    size="small"
                    type="text"
                    id="salesRep"
                    required
                    value={selectedCustomerInfo.salesRep || ""}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />)}
                </Grid>
      </Grid>
    ),
    shippingAddress: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    size="small"
                    type="text"
                    id="shippingAddress"
                    required
                    value={(outletsList?.length === 0 || ((fetchPricingBySalesrep && fetchPricingBySalesrep) && (selectedSalesRep?.length === 0))) ? "" : (selectedCustomerInfo.shippingAddress || "")}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
      </Grid>
    ),
    customer: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                {((fetchPricingBySalesrep && fetchPricingBySalesrep) ? selectedSalesRep?.trim()?.length !== 0 : true) ? (
                  <FormControl fullWidth size="small" className="custom_form_control">
                    <Autocomplete
                      options={outletsList}
                      getOptionLabel={(option) => {
                        if (option && option.outletCode && option.outletName) {
                          return `${option.outletName} - ${option.outletCode} ${option.approvalStatus === "PENDING" ? "(Approval Pending)" : ""}`;
                        }
                        return "";
                      }}
                      value={
                        outletsList.find(
                          (outlet) => outlet.outletCode === selectedCustomerInfo.outletCode
                        ) || null
                      }
                      disabled={isOutletListLengthValid}
                      clearIcon={isClearableEnabledOnOutlet ? undefined : null}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleMultiValueChange({
                            outletCode: newValue.outletCode ?? "",
                            salesRep: "",
                            selectedSalesRep: (fetchPricingBySalesrep && fetchPricingBySalesrep) ? selectedSalesRep : "",
                            shippingAddress: newValue.address ?? "",
                            customerChannel: newValue.channel ?? "",
                            paymentMethod: newValue.extendedAttributes?.preferredPaymentMode ?? "",
                            differentShippingAddress:
                              newValue.extendedAttributes?.shipTo?.address ?? "",
                            outletName: newValue.outletName ?? "",
                            activeStatus: newValue?.activeStatus ?? "",
                          });
                          isCounterSaleDMSOutlet.current = newValue?.extendedAttributes?.counterSalesOutletDMS ?? false
                          setSelectedOutletCode?.(newValue.outletCode ?? "")
                        } else {
                          setSelectedOutletCode?.("");
                          setSelectedBeat("");
                          setFilteredBeats(
                            Object.keys(beatRecord)
                              .filter(node => routeIdNameMap?.[node]) // keep only if present in map
                              .map(node => routeIdNameMap[node])
                          );
                          setOutletDetails?.(allOutletsList);
                          handleMultiValueChange({
                            outletCode: "",
                            beat: "",
                            salesRep: "",
                            selectedSalesRep: "",
                            shippingAddress: "",
                            customerChannel: "",
                            paymentMethod: "",
                            outletName: "",
                          });
                        }
                        if(!(fetchPricingBySalesrep && fetchPricingBySalesrep)) setSelectedSalesRep?.("");
                      }}
                      sx={{ ...customSx, ...autocompleteLableMarginFix }}
                      renderOption={(props, option) => (
                        <li {...props} style={{ color: "#464646", fontSize: "14px" }}>
                          {option.outletName} - {option.outletCode} {option.approvalStatus === "PENDING" ? "(Approval Pending)" : ""}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="skuCode"
                          label="Select Here"
                          variant="outlined"
                          size="small"
                          sx={{ ...customSx, ...autocompleteLableMarginFix }}
                        />
                      )}
                    />
                  </FormControl>
                  ) : (<TextField
                    size="small"
                    type="text"
                    id="outletCode"
                    required
                    value={""}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />)}
                </Grid>
      </Grid>
    ),
    customerChannel: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    size="small"
                    type="text"
                    id="customerChannel"
                    required
                    value={(outletsList?.length === 0 || ((fetchPricingBySalesrep && fetchPricingBySalesrep) && (selectedSalesRep?.length === 0))) ? "" : (selectedCustomerInfo.customerChannel || "")}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
      </Grid>
    ),
    beats: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                {((fetchPricingBySalesrep && fetchPricingBySalesrep) ? selectedSalesRep?.trim()?.length !== 0 : true) ? (
                  <FormControl fullWidth size="small" className="custom_form_control">
                    <Autocomplete
                      disableListWrap
                      options={beatList}
                      value={
                        ((selectedBeat?.length ?? 0) > 0) ? selectedBeat : null
                      }
                      disabled={isBeatListLengthValid}
                      clearIcon={isClearableEnabledOnBeat ? undefined : null}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedBeat(newValue);
                          handleMultiValueChange({
                            beat: beatNameToIdMap?.[newValue]
                          });
                        } else {
                          if(beatList?.length === 1 && outletsList?.length === 1) {
                            setSelectedOutletCode?.("");
                            setSelectedBeat("");
                            handleMultiValueChange({
                              outletCode: "",
                              beat: "",
                              salesRep: "",
                              selectedSalesRep: "",
                              shippingAddress: "",
                              customerChannel: "",
                              paymentMethod: "",
                              outletName: "",
                            });
                            setFilteredBeats(Object.keys(beatRecord)?.map(node => routeIdNameMap?.[node] ?? '-'));
                            setOutletDetails?.(allOutletsList);
                          } else {
                            setSelectedBeat("");
                            handleMultiValueChange({
                              beat: ""
                            });
                            setOutletDetails?.(allOutletsList);
                          }
                        }
                      }}
                      sx={{ ...customSx, ...autocompleteLableMarginFix }}
                      renderOption={(props, option) => (
                        <li {...props} style={{ color: "#464646", fontSize: "14px" }}>
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="skuCode"
                          label="Select Here"
                          variant="outlined"
                          size="small"
                          sx={{ ...customSx, ...autocompleteLableMarginFix }}
                        />
                      )}
                    />
                  </FormControl>
                  ) : (<TextField
                    size="small"
                    type="text"
                    id="outletCode"
                    required
                    value={""}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />)}
                </Grid>
      </Grid>
    ),
    paymentMethod: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    size="small"
                    type="text"
                    id="paymentMethod"
                    required
                    value={(outletsList?.length === 0 || ((fetchPricingBySalesrep && fetchPricingBySalesrep) && (selectedSalesRep?.length === 0))) ? "" : (selectedCustomerInfo.paymentMethod || "")}
                    onChange={(e) => handleChange(e.target.id, e.target.value)}
                    className="select_customer_textfield"
                    sx={customSx}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
      </Grid>
    ),
    billDate: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Bill Date:"
                      disabled
                      inputFormat="DD.MM.YYYY"
                      value={(outletsList?.length !== 0 && outletsList.find((outlet) => outlet.outletCode === selectedCustomerInfo.outletCode) && ((fetchPricingBySalesrep && fetchPricingBySalesrep) ? (selectedSalesRep?.length !== 0) : true)) ? dayjs() : null}
                      onChange={(value) => {}}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          disabled
                          sx={secondaryOrderDatePickerSx}
                          size="small"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
      </Grid>
    ),
    deliveryDate: (configValue: ConfigValue) => (
      <Grid container className="customer_selection_grid_item" xs={4} alignItems="center">
                <Grid item xs={5}>
                  <span className="customer_labels">{configValue.label}</span>
                </Grid>
                <Grid item xs={7}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Delivery Date:"
                      inputFormat="DD.MM.YYYY"
                      disabled
                      value={(outletsList?.length !== 0 && outletsList.find((outlet) => outlet.outletCode === selectedCustomerInfo.outletCode) && ((fetchPricingBySalesrep && fetchPricingBySalesrep) ? (selectedSalesRep?.length !== 0) : true)) 
  ? dayjs(selectedCustomerInfo.deliveryDate, 'DD/MM/YY') 
  : null}
                      minDate={dayjs()}
                      onChange={(newValue) => {
                        handleChange("deliveryDate", dayjs(newValue).format("DD/MM/YY"));
                      }}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          inputProps={{
                            ...props.inputProps,
                            readOnly: true,
                          }}
                          disabled
                          sx={secondaryOrderDatePickerSx}
                          size="small"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
      </Grid>
    ),
  };

  return (
    <div className="select_customer_container">
      <>
        <Accordion
          expanded={expandForm || !selectedCustomerInfo?.outletCode}
          sx={{
            border: "none !important",
            boxShadow: "none !important",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            onClick={() => setExpandForm(!expandForm)}
             sx={{
              padding: "0 !important",
              backgroundColor: "#fff !important",
              margin: "0 !important",
              minHeight: "0 !important",
              height: "0 !important",
              paddingTop: "20px !important",
              paddingBottom: "20px !important",
            }}
          >
            <DefaultHeading
              heading="Select Customer"
              customFontSize="16.5px"
              customFontColor="#000"
              createSecondaryOrderHeading={true}
            />
          </AccordionSummary>
          <AccordionDetails
            sx={{
              border: "none !important",
            }}
          >
          <Grid container className="customer_selection_grid">
          {Object.entries(createSecondaryOrderConfig)
            .filter(([key, value]) => value.enable) // Only include enabled fields
            .sort((a, b) => a[1].index - b[1].index) // Sort by index in ascending order
            .map(([fieldKey, field]) => (
              <React.Fragment key={fieldKey}>
                {renderFieldMap[fieldKey as keyof typeof renderFieldMap](field)}
              </React.Fragment>
          ))}
          </Grid>
          </AccordionDetails>
        </Accordion>
        <Modal
            open={counterSalesOutletFormModal}
            onClose={(event, reason) => {
              // Only close if the close button is clicked, not on backdrop click
              if (reason !== 'backdropClick') {
                handleCounterSalesOutletFormClose();
              }
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            disableEscapeKeyDown={true}
            >
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            overflow: 'visible',
            outline: 'none' , // Removes the default focus outline
            backgroundColor: 'transparent',
            borderRadius: '8px'
         }}>
          <OutletForm onClose={handleCounterSalesOutletFormClose} onSaveOutletForm={handleOutletCreate}/>
          </div>
      </Modal>
      </>
    </div>
  );
};

export default SelectCustomer;
